/**
 * PersonnelAppointmentDetailModal 컴포넌트 (V70)
 *
 * **역할:**
 * - 진급후보 산출 리스트에서 직원 이름 클릭 시 표시되는 인사발령 전용 상세 팝업
 * - 직위 발령일, 조기진급 설정 등 인사발령 관련 정보 조회/수정
 *
 * **V70 변경사항:**
 * - 토스트 알림 추가 (스크롤 위치 무관, 우하단 고정)
 * - 에러/성공 메시지 모두 토스트로 즉시 표시
 * - 3.5초 자동 닫힘 + 닫기(X) 버튼
 *
 * **V69 변경사항:**
 * - 중복 예약 에러(already-exists) UI 표시 추가
 * - 상단 직원 정보 UI 개선 (좌측정렬, 2줄 구조, pill 배지)
 *
 * **V67 변경사항:**
 * - UI: 아이콘 제거
 * - 이전경력 step=1 (정수)
 * - 인정 학력 표시 추가 (promotionEducationLevel/Years 우선)
 * - 직위 발령일 영역 2개 분리 (현재 + 다음)
 * - 조기진급 설정 + saveEarlyPromotion 백엔드 연동
 * - 날짜는 반드시 YYYY-01-01만 허용
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, Loader, Calendar, Clock, AlertCircle } from 'lucide-react';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';
import { PromotionCandidate } from '../../../shared/types/promotionCandidate';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase/config';
import { calculateYearsBetween } from '../../../shared/utils/dateUtils';
import { getEducationLevelLabelSafe } from '../../../shared/utils/educationLevel';
import { formatYearsMonths } from '../../../shared/utils/formatYearsMonths';  // V79

/**
 * PersonnelAppointmentDetailModal Props
 */
export interface PersonnelAppointmentDetailModalProps {
  isOpen: boolean;
  candidate: PromotionCandidate | null;
  referenceDate: string;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * V62: 학력 코드 → 라벨 변환 (공통 유틸 사용)
 * - 레거시 코드(MASTER, BACHELOR, ASSOCIATE_*) 자동 정규화
 * - 인정연한(년) 포함 표기
 */
function getEducationLevelLabel(code: string | undefined): string {
  if (!code) return '-';
  const label = getEducationLevelLabelSafe(code);
  if (label === '-') return code; // 알 수 없는 코드는 원문 표시
  // 인정연한 표시용 매핑
  const yearsMap: Record<string, number> = {
    '고졸': 0, '초대졸(2년)': 2, '초대졸(3년)': 3, '대졸': 4, '대학원졸': 6,
  };
  const years = yearsMap[label];
  return years !== undefined ? `${label} (${years}년)` : label;
}

/**
 * 다음 진급 가능 연도 계산 (YYYY-01-01 형식)
 *
 * @param recognizedYears 현재 인정근무연한
 * @param requiredYears 다음 직위 필요 인정근무연한
 * @param referenceDate 기준일 (YYYY-01-01)
 * @returns 다음 진급 가능 연도 (YYYY-01-01)
 */
function calculateNextPromotionDate(
  recognizedYears: number,
  requiredYears: number | null,
  referenceDate: string
): string | null {
  if (requiredYears === null) return null;

  const referenceYear = parseInt(referenceDate.substring(0, 4), 10);

  // 이미 충족한 경우: 기준연도
  if (recognizedYears >= requiredYears) {
    return `${referenceYear}-01-01`;
  }

  // 부족분 계산 후 최초 충족 시점 (매년 1씩 증가 가정)
  const yearsNeeded = Math.ceil(requiredYears - recognizedYears);
  const targetYear = referenceYear + yearsNeeded;

  return `${targetYear}-01-01`;
}

/**
 * PersonnelAppointmentDetailModal 컴포넌트
 */
export function PersonnelAppointmentDetailModal({
  isOpen,
  candidate,
  referenceDate,
  onClose,
  onSuccess,
}: PersonnelAppointmentDetailModalProps) {
  // ============================================
  // 상태
  // ============================================
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // V70: 토스트 상태 (스크롤 위치 무관하게 표시)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // 폼 데이터
  const [priorCareerYears, setPriorCareerYears] = useState<number>(0);
  const [promotionBonusYears, setPromotionBonusYears] = useState<number>(0);

  // 조기진급 설정
  const [isEarlyPromotion, setIsEarlyPromotion] = useState(false);
  const [earlyPromotionDate, setEarlyPromotionDate] = useState('');
  const [earlyPromotionBonusYears, setEarlyPromotionBonusYears] = useState<number>(0);
  const [earlyPromotionReason, setEarlyPromotionReason] = useState('');

  // ============================================
  // 계산된 값
  // ============================================
  const calculatedValues = useMemo(() => {
    if (!candidate || !referenceDate) {
      return {
        educationYears: 0,
        workYears: 0,
        recognizedYears: 0,
        positionYears: 0,
        nextPromotionDate: null,
        requiredYears: null,
        shortfall: 0,
      };
    }

    // V67: 인정 학력 (promotionEducationYears 우선, 없으면 educationYears fallback)
    const educationYears =
      (candidate.employee as any).promotionEducationYears ??
      candidate.employee.educationYears ??
      0;

    // V67: 근속 연수 = BE candidate.recognizedYearsBreakdown.workYears (휴직 차감 반영된 SSoT 값)
    //   기존 FE 재계산 (calculateYearsBetween(regularStartDate, referenceDate)) 은 휴직 무시 →
    //   페이지 목록(BE) 과 모달(FE) 값이 갈라짐. V67 에서 BE SSoT 사용으로 통일.
    //   regularStartDate 기반 폴백은 유지 (BE breakdown 누락 시 회귀 방어).
    const regularStartDate = candidate.regularStartDate;
    const beWorkYears = candidate.recognizedYearsBreakdown?.workYears;
    const workYears =
      typeof beWorkYears === 'number'
        ? beWorkYears
        : regularStartDate
          ? calculateYearsBetween(regularStartDate, referenceDate)
          : 0;

    // 인정근무연한 = 학력 + 이전경력 + 근속(BE SSoT) + 가산
    //   priorCareerYears / promotionBonusYears 는 모달 입력 편집 가능 → FE 합산.
    //   workYears 는 BE 산식과 동일 SSoT 사용 → 페이지 목록 / BE candidate / 모달 합계 일치.
    const recognizedYears =
      educationYears + priorCareerYears + workYears + promotionBonusYears;

    // 직위 연차
    const positionYears = candidate.displayPositionStartDate
      ? calculateYearsBetween(candidate.displayPositionStartDate, referenceDate)
      : 0;

    // 다음 직위 필요 연한
    const requiredYears = candidate.nextPosition?.requiredRecognizedYearsFromHire ?? null;

    // 다음 진급 가능 연도
    const nextPromotionDate = calculateNextPromotionDate(
      recognizedYears,
      requiredYears,
      referenceDate
    );

    // 부족분 계산 (조기진급 기본 가산값)
    const shortfall =
      requiredYears !== null ? Math.max(0, Math.ceil(requiredYears - recognizedYears)) : 0;

    return {
      educationYears,
      workYears: Math.max(0, workYears),
      recognizedYears: Math.round(recognizedYears * 10) / 10,
      positionYears: Math.max(0, positionYears),
      nextPromotionDate,
      requiredYears,
      shortfall,
    };
  }, [candidate, referenceDate, priorCareerYears, promotionBonusYears]);

  // ============================================
  // 초기화
  // ============================================
  useEffect(() => {
    if (!isOpen || !candidate) {
      return;
    }

    // 이전경력 초기값
    setPriorCareerYears(candidate.employee.priorCareerYears || 0);

    // 가산 초기값
    setPromotionBonusYears(candidate.employee.promotionBonusYears || 0);

    // 조기진급 설정 초기화
    setIsEarlyPromotion(false);
    setEarlyPromotionDate('');
    setEarlyPromotionBonusYears(0);
    setEarlyPromotionReason('');

    setError(null);
    setSuccessMessage(null);
    setToast(null); // V70: 토스트 초기화
  }, [isOpen, candidate]);

  // V70: 토스트 자동 닫힘 (3.5초)
  useEffect(() => {
    if (!toast) return;

    const timer = setTimeout(() => {
      setToast(null);
    }, 3500);

    return () => clearTimeout(timer);
  }, [toast]);

  // 조기진급 활성화 시 기본값 설정
  useEffect(() => {
    if (isEarlyPromotion && candidate) {
      // 다음 진급 가능 연도 또는 기준연도를 기본값으로
      const defaultDate =
        calculatedValues.nextPromotionDate || `${referenceDate.substring(0, 4)}-01-01`;
      setEarlyPromotionDate(defaultDate);

      // 부족분을 기본 가산값으로
      setEarlyPromotionBonusYears(calculatedValues.shortfall);
    }
  }, [isEarlyPromotion, candidate, calculatedValues.nextPromotionDate, calculatedValues.shortfall, referenceDate]);

  // ============================================
  // 저장 핸들러 (조기진급)
  // ============================================
  const handleSaveEarlyPromotion = async () => {
    if (!candidate || !candidate.nextPosition) return;

    // 유효성 검사: 날짜 형식 (YYYY-MM-DD)
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!datePattern.test(earlyPromotionDate)) {
      setError('발령일은 YYYY-MM-DD 형식으로 입력해주세요.');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (!functions) {
        throw new Error('Firebase Functions가 초기화되지 않았습니다.');
      }

      // V67: saveEarlyPromotion 백엔드 호출
      const saveEarlyPromotionFn = httpsCallable<
        {
          employeeId: string;
          fromPositionId: string;
          toPositionId: string;
          effectiveDate: string;
          promotionBonusYearsToAdd: number;
          reason?: string;
        },
        { success: boolean; message: string; appointmentId: string; isExecuted: boolean }
      >(functions, 'saveEarlyPromotion');

      const result = await saveEarlyPromotionFn({
        employeeId: candidate.employee.id,
        fromPositionId: candidate.currentPosition.id,
        toPositionId: candidate.nextPosition.id,
        effectiveDate: earlyPromotionDate,
        promotionBonusYearsToAdd: earlyPromotionBonusYears,
        reason: earlyPromotionReason || undefined,
      });

      if (!result.data.success) {
        throw new Error(result.data.message || '저장에 실패했습니다.');
      }

      // V70: 성공 토스트 표시
      const successMsg = result.data.message || '조기진급이 저장되었습니다.';
      setSuccessMessage(successMsg);
      setToast({ type: 'success', message: successMsg });

      // 2초 후 닫기 및 리프레시
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('[V70] 조기진급 저장 실패:', err);

      // V70: Firebase callable 에러 코드 처리
      const errorCode = err?.code || (err as any)?.details?.code || '';
      const errorMessage = err?.message || '';

      let displayError: string;

      // already-exists 에러 (중복 예약)
      if (
        errorCode === 'already-exists' ||
        errorCode === 'functions/already-exists' ||
        errorMessage.includes('이미 예약된')
      ) {
        displayError = errorMessage || '이미 예약된 조기진급 발령이 있습니다.';
      } else {
        // 기타 에러
        displayError = errorMessage || '저장 중 오류가 발생했습니다.';
      }

      setError(displayError);
      setToast({ type: 'error', message: displayError }); // V70: 에러 토스트
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // 기존 정보 저장 핸들러 (이전경력/가산만)
  // ============================================
  const handleSaveBasicInfo = async () => {
    if (!candidate) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (!functions) {
        throw new Error('Firebase Functions가 초기화되지 않았습니다.');
      }

      const updateFn = httpsCallable<
        {
          employeeId: string;
          currentPositionStartDate: string | null;
          priorCareerYears?: number;
          promotionBonusYears?: number;
        },
        { success: boolean; message: string }
      >(functions, 'updateEmployeeEditableFields');

      const result = await updateFn({
        employeeId: candidate.employee.id,
        currentPositionStartDate: null, // 정책 기반 자동 계산 유지
        priorCareerYears: Math.floor(priorCareerYears), // V67: 정수로 저장
        promotionBonusYears: promotionBonusYears,
      });

      if (!result.data.success) {
        throw new Error(result.data.message || '저장에 실패했습니다.');
      }

      // V70: 성공 토스트 표시
      const successMsg = '저장되었습니다.';
      setSuccessMessage(successMsg);
      setToast({ type: 'success', message: successMsg });

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('[V70] 기본 정보 저장 실패:', err);
      const displayError = err.message || '저장 중 오류가 발생했습니다.';
      setError(displayError);
      setToast({ type: 'error', message: displayError }); // V70: 에러 토스트
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================
  // 닫기 핸들러
  // ============================================
  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const overlayProps = useModalOverlayClose(handleClose);

  // ============================================
  // 렌더링
  // ============================================
  if (!isOpen || !candidate) {
    return null;
  }

  // V67: 인정 학력 (promotionEducationLevel 우선, V62 BE 필드 추가)
  const recognizedEducationLevel =
    candidate.employee.promotionEducationLevel ||
    candidate.employee.educationLevel;

  return (
    <>
      {/* V70: 토스트 (스크롤 위치 무관, 우하단 고정) */}
      {toast && (
        <div
          style={{
            ...styles.toastContainer,
            ...(toast.type === 'success' ? styles.toastSuccess : styles.toastError),
          }}
        >
          <span style={styles.toastMessage}>{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            style={styles.toastClose}
            aria-label="닫기"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div style={styles.overlay} {...overlayProps}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* V67: 헤더 - 아이콘 제거 */}
        <div style={styles.header}>
          <h2 style={styles.title}>인사발령 상세</h2>
          <button onClick={handleClose} disabled={isSaving} style={styles.closeButton}>
            <X size={24} />
          </button>
        </div>

        {/* 본문 */}
        <div style={styles.body}>
          {/* 성공 메시지 */}
          {successMessage && (
            <div style={styles.successBox}>
              <strong>완료:</strong> {successMessage}
            </div>
          )}

          {/* 에러 메시지 */}
          {error && (
            <div style={styles.errorBox}>
              <AlertCircle size={16} style={{ marginRight: '8px' }} />
              <strong>오류:</strong> {error}
            </div>
          )}

          {/* V69: 직원 정보 헤더 - 좌측정렬 카드형 */}
          <div style={styles.employeeHeader}>
            {/* 1줄: 직원명 + 사번 */}
            <div style={styles.employeeHeaderRow1}>
              <span style={styles.employeeName}>{candidate.employee.nameKo}</span>
              {candidate.employee.employeeNumber && (
                <span style={styles.employeeNumber}>
                  {candidate.employee.employeeNumber}
                </span>
              )}
            </div>
            {/* 2줄: 조직 / 현재직위 pill */}
            <div style={styles.employeeHeaderRow2}>
              <span style={styles.pill}>
                {candidate.employee.orgUnitName || '조직 미지정'}
              </span>
              <span style={styles.pillDivider}>/</span>
              <span style={styles.pill}>
                {candidate.currentPosition.name}
              </span>
            </div>
          </div>

          {/* V67: 인정 학력 표시 */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>인정 학력</h3>
            <div style={styles.field}>
              <div style={styles.readOnlyValue}>
                {getEducationLevelLabel(recognizedEducationLevel)}
              </div>
              <p style={styles.helpText}>
                입사 시점에 고정된 학력으로, 직원관리에서 학력을 수정해도 진급 계산에는 영향이
                없습니다.
              </p>
            </div>
          </div>

          {/* 인사발령 정보 */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>인사발령 정보</h3>

            {/* 정규직 시작일 */}
            <div style={styles.field}>
              <label style={styles.label}>
                <Calendar size={16} style={{ marginRight: '4px' }} />
                정규직 시작일
              </label>
              <div style={styles.readOnlyValue}>{candidate.regularStartDate || '-'}</div>
            </div>

            {/* V67: 이전경력 (step=1, 정수) */}
            <div style={styles.field}>
              <label style={styles.label}>이전경력 (년)</label>
              <input
                type="number"
                value={priorCareerYears}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setPriorCareerYears(isNaN(val) || val < 0 ? 0 : val);
                }}
                step="1"
                min="0"
                placeholder="0"
                style={styles.input}
              />
            </div>

            {/* 가산 */}
            <div style={styles.field}>
              <label style={styles.label}>가산 (년)</label>
              <input
                type="number"
                value={promotionBonusYears}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setPromotionBonusYears(isNaN(val) || val < 0 ? 0 : val);
                }}
                step="1"
                min="0"
                placeholder="0"
                style={styles.input}
              />
            </div>

            {/* 인정 근무연한 */}
            <div style={styles.field}>
              <label style={styles.label}>
                <Clock size={16} style={{ marginRight: '4px' }} />
                인정 근무연한 (학력+이전경력+근속+가산)
              </label>
              <div style={styles.readOnlyValue}>
                {calculatedValues.educationYears}+{priorCareerYears}+
                {calculatedValues.workYears}+{promotionBonusYears} (
                {formatYearsMonths(calculatedValues.recognizedYears)})
              </div>
            </div>
          </div>

          {/* V67: 직위 발령일 영역 2개 분리 */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>직위 발령 정보</h3>

            {/* 4-1: 현재 직위 발령일 */}
            <div style={styles.field}>
              <label style={styles.label}>
                현재 직위 발령일 ({candidate.currentPosition.name})
              </label>
              <div style={styles.readOnlyValue}>
                {candidate.displayPositionStartDate || '(정책 기반 자동 계산)'}
                {candidate.displayPositionStartDate && (
                  <span style={{ marginLeft: '8px', color: 'var(--color-gray-500)' }}>
                    (직위 연차: {calculatedValues.positionYears}년)
                  </span>
                )}
              </div>
            </div>

            {/* 4-2: 다음 직위 발령일 */}
            {candidate.nextPosition && (
              <div style={styles.field}>
                <label style={styles.label}>
                  다음 직위 예상 발령일 ({candidate.nextPosition.name})
                </label>
                <div style={styles.readOnlyValue}>
                  {calculatedValues.nextPromotionDate || '-'}
                  {calculatedValues.requiredYears !== null && (
                    <span style={{ marginLeft: '8px', color: 'var(--color-gray-500)' }}>
                      (필요 인정근무연한: {calculatedValues.requiredYears}년)
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* 진급 충족 여부 */}
            <div style={styles.field}>
              <label style={styles.label}>진급 충족 여부</label>
              <div
                style={{
                  ...styles.readOnlyValue,
                  color: candidate.meetsCriteria ? '#4CAF50' : '#9E9E9E',
                  fontWeight: '600',
                }}
              >
                {candidate.meetsCriteria ? '충족' : '미충족'}
                {!candidate.meetsCriteria && calculatedValues.shortfall > 0 && (
                  <span style={{ marginLeft: '8px', fontWeight: 'normal' }}>
                    (부족: {calculatedValues.shortfall}년)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* V67: 조기진급 설정 */}
          {candidate.nextPosition && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>조기진급 설정</h3>

              {/* 조기진급 체크박스 */}
              <div style={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id="earlyPromotion"
                  checked={isEarlyPromotion}
                  onChange={() => setIsEarlyPromotion(!isEarlyPromotion)}
                  style={styles.checkbox}
                />
                <label htmlFor="earlyPromotion" style={styles.checkboxLabel}>
                  조기진급 설정 (다음 직위로 발령)
                </label>
              </div>

              {isEarlyPromotion && (
                <>
                  {/* 다음 직위 발령일 입력 */}
                  <div style={styles.field}>
                    <label style={styles.label}>
                      다음 직위 발령일 ({candidate.nextPosition.name})
                      <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="date"
                      value={earlyPromotionDate}
                      onChange={(e) => setEarlyPromotionDate(e.target.value)}
                      style={styles.input}
                    />
                  </div>

                  {/* 가산 연한 입력 */}
                  <div style={styles.field}>
                    <label style={styles.label}>
                      추가 가산 연한 (정수)
                      <span style={styles.hint}> (자동 계산된 부족분: {calculatedValues.shortfall}년)</span>
                    </label>
                    <input
                      type="number"
                      value={earlyPromotionBonusYears}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setEarlyPromotionBonusYears(isNaN(val) || val < 0 ? 0 : val);
                      }}
                      step="1"
                      min="0"
                      placeholder="0"
                      style={styles.input}
                    />
                    <p style={styles.helpText}>
                      조기진급 시 기존 가산에 누적됩니다. 현재 가산: {promotionBonusYears}년 →
                      저장 후: {promotionBonusYears + earlyPromotionBonusYears}년
                    </p>
                  </div>

                  {/* 조기진급 사유 */}
                  <div style={styles.field}>
                    <label style={styles.label}>
                      조기진급 사유 <span style={styles.optional}>(선택)</span>
                    </label>
                    <textarea
                      value={earlyPromotionReason}
                      onChange={(e) => setEarlyPromotionReason(e.target.value)}
                      placeholder="조기진급 사유를 입력하세요"
                      rows={2}
                      style={styles.textarea}
                    />
                  </div>

                  {/* 안내 문구 */}
                  <div style={styles.infoBox}>
                    <strong>조기진급 모드:</strong>
                    <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                      <li>과거/오늘 날짜: 즉시 반영 (users 문서 업데이트)</li>
                      <li>미래 날짜: 예약 저장 (스케줄러 미구현)</li>
                      <li>직위가 {candidate.nextPosition.name}(으)로 변경됩니다.</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div style={styles.footer}>
          <button onClick={handleClose} disabled={isSaving} style={styles.cancelButton}>
            취소
          </button>

          {isEarlyPromotion ? (
            <button
              onClick={handleSaveEarlyPromotion}
              disabled={isSaving || !earlyPromotionDate}
              style={{
                ...styles.saveButton,
                backgroundColor: '#FF9800',
              }}
            >
              {isSaving ? (
                <>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  저장 중...
                </>
              ) : (
                <>
                  <Save size={18} />
                  조기진급 저장
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleSaveBasicInfo}
              disabled={isSaving}
              style={styles.saveButton}
            >
              {isSaving ? (
                <>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  저장 중...
                </>
              ) : (
                <>
                  <Save size={18} />
                  저장
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

/**
 * 스타일
 */
const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid var(--color-gray-200)',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-gray-500)',
    padding: '4px',
  },
  body: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '24px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid var(--color-gray-200)',
  },
  // V69: 직원 정보 헤더 스타일
  employeeHeader: {
    padding: '16px',
    marginBottom: '24px',
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: '8px',
    border: '1px solid var(--color-gray-200)',
  },
  employeeHeaderRow1: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    marginBottom: '8px',
  },
  employeeName: {
    fontSize: '22px',
    fontWeight: '700',
    color: 'var(--color-gray-900)',
  },
  employeeNumber: {
    fontSize: '14px',
    fontWeight: '400',
    color: 'var(--color-gray-500)',
  },
  employeeHeaderRow2: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pill: {
    display: 'inline-block',
    padding: '4px 10px',
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '16px',
  },
  pillDivider: {
    color: 'var(--color-gray-400)',
    fontSize: '14px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    marginBottom: '8px',
  },
  hint: {
    fontSize: '12px',
    fontWeight: 'normal',
    color: 'var(--color-gray-500)',
  },
  optional: {
    fontSize: '12px',
    fontWeight: 'normal',
    color: 'var(--color-gray-400)',
  },
  required: {
    color: '#F44336',
    marginLeft: '4px',
  },
  readOnlyValue: {
    fontSize: '14px',
    color: 'var(--color-gray-800)',
    padding: '10px 12px',
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: '6px',
    border: '1px solid var(--color-gray-200)',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  helpText: {
    fontSize: '12px',
    color: 'var(--color-gray-500)',
    marginTop: '4px',
    marginBottom: 0,
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    marginRight: '8px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    cursor: 'pointer',
  },
  infoBox: {
    padding: '12px 16px',
    backgroundColor: '#FFF3E0',
    borderLeft: '4px solid #FF9800',
    borderRadius: '6px',
    fontSize: '13px',
    color: 'var(--color-gray-700)',
    lineHeight: 1.6,
  },
  successBox: {
    padding: '12px 16px',
    backgroundColor: '#E8F5E9',
    borderLeft: '4px solid #4CAF50',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#2E7D32',
    marginBottom: '24px',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#FFEBEE',
    borderLeft: '4px solid #F44336',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#C62828',
    marginBottom: '24px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid var(--color-gray-200)',
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  // V70: 토스트 스타일
  toastContainer: {
    position: 'fixed' as const,
    bottom: '24px',
    right: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 20px',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 9999,
    maxWidth: '400px',
    animation: 'slideIn 0.3s ease-out',
  },
  toastSuccess: {
    backgroundColor: '#4CAF50',
    color: 'white',
  },
  toastError: {
    backgroundColor: '#F44336',
    color: 'white',
  },
  toastMessage: {
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: 1.4,
    flex: 1,
  },
  toastClose: {
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.8,
  },
};
